// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/carousel.js
import BaseComponent from "./base_component"

export default class Carousel extends BaseComponent {
  static componentName = "carousel"
  static defaults = { interval: 5000, ride: false, wrap: true, keyboard: true, pause: "hover", touch: true }

  constructor(element, config = {}) {
    super(element, config)
    this.index = Math.max(
      0,
      this.items.findIndex((item) => item.classList.contains("active")),
    )
    this.listen(element, "keydown", (event) => {
      if (
        !this.config.keyboard ||
        /input|textarea/i.test(event.target.tagName) ||
        event.altKey ||
        event.metaKey ||
        event.ctrlKey
      )
        return
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return
      event.preventDefault()
      const forward = (event.key === "ArrowRight") !== (getComputedStyle(element).direction === "rtl")
      forward ? this.next() : this.prev()
    })
    if (this.config.pause === "hover") {
      this.listen(element, "mouseenter", () => this.pause())
      this.listen(element, "mouseleave", () => this.resume())
      this.listen(element, "focusin", () => this.pause())
      this.listen(element, "focusout", (event) => {
        if (!element.contains(event.relatedTarget)) this.resume()
      })
    }
    if (this.config.touch) {
      this.listen(element, "pointerdown", (event) => {
        if (event.pointerType !== "mouse") this.touchStart = event.clientX
      })
      this.listen(element, "pointerup", (event) => {
        if (this.touchStart === undefined) return
        const distance = event.clientX - this.touchStart
        this.touchStart = undefined
        if (Math.abs(distance) > 40)
          distance < 0 !== (getComputedStyle(element).direction === "rtl") ? this.next() : this.prev()
        this.pause()
        clearTimeout(this.touchTimeout)
        this.touchTimeout = setTimeout(() => this.resume(), 500 + this.delay())
      })
      this.listen(element, "pointercancel", () => {
        this.touchStart = undefined
      })
    }
    this.riding = Boolean(this.config.ride)
    if (this.riding) this.cycle()
  }

  get items() {
    return [...this.element.querySelectorAll(".carousel-item")]
  }
  delay() {
    return Number(this.items[this.index]?.dataset.coreuiInterval || this.config.interval) || 5000
  }

  to(index) {
    const items = this.items
    if (!items.length || this.transitioning || index < 0 || index >= items.length || index === this.index) return
    const from = this.index
    const forward = index > from
    const rtl = getComputedStyle(this.element).direction === "rtl"
    const detail = { from, to: index, relatedTarget: items[index], direction: forward !== rtl ? "left" : "right" }
    if (this.emit("slide", detail, { cancelable: true }).defaultPrevented) return
    clearTimeout(this.interval)
    this.transitioning = true
    const old = items[from]
    const next = items[index]
    const order = forward ? "carousel-item-next" : "carousel-item-prev"
    const movement = forward ? "carousel-item-start" : "carousel-item-end"
    this.index = index
    next.classList.add(order)
    next.offsetHeight
    old.classList.add(movement)
    next.classList.add(movement)
    this.element.querySelectorAll(".carousel-indicators [data-coreui-slide-to]").forEach((indicator, i) => {
      indicator.classList.toggle("active", i === index)
      if (i === index) indicator.setAttribute("aria-current", "true")
      else indicator.removeAttribute("aria-current")
    })
    this.afterTransition(
      old,
      () => {
        old.classList.remove("active", movement)
        next.classList.remove(order, movement)
        next.classList.add("active")
        this.transitioning = false
        this.emit("slid", detail)
        if (this.riding && !this.paused) this.cycle()
      },
      this.element.classList.contains("slide"),
    )
  }

  next() {
    this.to(this.index + 1 < this.items.length ? this.index + 1 : this.config.wrap ? 0 : this.index)
  }
  prev() {
    this.to(this.index > 0 ? this.index - 1 : this.config.wrap ? this.items.length - 1 : this.index)
  }
  pause() {
    this.paused = true
    clearTimeout(this.interval)
  }
  resume() {
    this.paused = false
    if (this.riding) this.cycle()
  }

  cycle() {
    clearTimeout(this.interval)
    this.riding = true
    if (this.config.interval === false || this.paused) return
    this.interval = setTimeout(() => {
      if (!document.hidden && this.element.getClientRects().length) this.next()
      if (!this.transitioning) this.cycle()
    }, this.delay())
  }

  dispose() {
    if (this.signal.aborted) return
    clearTimeout(this.interval)
    clearTimeout(this.touchTimeout)
    this.items.forEach((item, index) => {
      item.classList.remove("carousel-item-next", "carousel-item-prev", "carousel-item-start", "carousel-item-end")
      item.classList.toggle("active", index === this.index)
    })
    super.dispose()
  }
}
